import { IconCheck, IconPencil } from '@tabler/icons-react-native';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button } from 'app/design/primitives/button';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import { useTheme } from 'app/shopify/theme';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import Animated, { useReducedMotion, type SharedValue } from 'react-native-reanimated';
import { useConfettiParticleStyle, useFooterCelebration } from './posts-feed-footer-motion';

const confettiTargets = [
  [-140, 30],
  [-110, -15],
  [-80, 80],
  [-45, -28],
  [-20, 110],
  [25, -25],
  [55, 85],
  [85, -5],
  [115, 65],
  [145, 20],
  [-125, 115],
  [-60, 145],
  [65, 145],
  [130, 120],
] as const;

function ConfettiParticle({
  progress,
  x,
  y,
  index,
  color,
}: {
  progress: SharedValue<number>;
  x: number;
  y: number;
  index: number;
  color?: string;
}) {
  const motion = useConfettiParticleStyle(progress, x, y, index);
  const round = index % 3 === 0;
  return (
    <Animated.View
      accessible={false}
      style={[
        styles.confettiParticle,
        { backgroundColor: color, height: round ? 7 : 12, borderRadius: round ? 4 : 2 },
        motion,
      ]}
    />
  );
}

function SuccessConfetti({ progress }: { progress: SharedValue<number> }) {
  const theme = useTheme();
  const colors = [
    theme.colors['surface.brand.default'],
    theme.colors['surface.brand.secondary-2.default'],
    theme.colors['surface.brand.secondary-3.default'],
    theme.colors['surface.brand.secondary-5.default'],
  ];
  return (
    <View
      testID="posts-feed-success-confetti"
      pointerEvents="none"
      accessible={false}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={styles.confettiLayer}
    >
      {confettiTargets.map(([x, y], index) => (
        <ConfettiParticle
          key={`${x},${y}`}
          progress={progress}
          x={x}
          y={y}
          index={index}
          color={colors[index % colors.length]}
        />
      ))}
    </View>
  );
}

export function PostsFeedFooter({ animate = false }: { animate?: boolean }) {
  const { t } = useTranslation('posts');
  const theme = useTheme();
  const navigation = useNavigation();
  const { canUseSocialFeed } = useSocialFeedFeature();
  const reducedMotion = useReducedMotion();
  const shouldAnimate = animate && !reducedMotion;
  const motion = useFooterCelebration(shouldAnimate);

  return (
    <View testID="posts-feed-footer" style={styles.container}>
      <Animated.View
        testID="posts-feed-completion-card"
        style={[
          styles.card,
          { backgroundColor: theme.colors['surface.secondary-neutral'], borderColor: theme.colors['border.secondary'] },
        ]}
      >
        {!reducedMotion ? <SuccessConfetti progress={motion.progress} /> : null}
        <View style={[styles.badge, { backgroundColor: theme.colors['surface.brand.subtle'] }]}>
          <View
            style={styles.checkFrame}
            accessible={false}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
          >
            {!reducedMotion ? (
              <Animated.View
                testID="posts-feed-success-ring"
                pointerEvents="none"
                accessible={false}
                style={[styles.successRing, { borderColor: theme.colors['text.emphasized'] }, motion.ringStyle]}
              />
            ) : null}
            <Animated.View testID="posts-feed-success-check" style={motion.checkStyle}>
              <IconCheck size={22} color={theme.colors['text.emphasized']} strokeWidth={2.5} />
            </Animated.View>
          </View>
          <Text color="text.brand" fontWeight="600" textAlign="center" style={styles.badgeText}>
            {t('feed.endOfFeed.status')}
          </Text>
        </View>
        <Animated.View testID="posts-feed-artwork-motion" style={[styles.artworkFrame, motion.artworkStyle]}>
          <PCImage
            testID="posts-feed-artwork-image"
            accessible={false}
            contentFit="contain"
            source={require('app/design/images/social-feed/end-of-feed.png')}
            style={styles.artwork}
          />
        </Animated.View>
        <Animated.View testID="posts-feed-title-motion" style={[styles.copyFrame, motion.titleStyle]}>
          <Text
            accessibilityRole="header"
            variant="headerDisplay2"
            color="text.emphasized"
            fontWeight="900"
            textAlign="center"
            style={styles.title}
          >
            {t('feed.endOfFeed.title')}
          </Text>
        </Animated.View>
        <Animated.View testID="posts-feed-description-motion" style={[styles.copyFrame, motion.descriptionStyle]}>
          <Text color="text.emphasized" fontWeight="500" textAlign="center" style={styles.description}>
            {t('feed.endOfFeed.description')}
          </Text>
        </Animated.View>
        {canUseSocialFeed ? (
          <Animated.View testID="posts-feed-action-motion" style={[styles.actionFrame, motion.actionStyle]}>
            <Button
              testID="posts-feed-create-story"
              variant="primary"
              accessibilityLabel={t('feed.endOfFeed.action')}
              accessibilityHint={t('feed.endOfFeed.actionHint')}
              onPress={() => navigation.navigate('story-picker')}
              prefix={
                <IconPencil size={26} color={theme.colors['icon.inverse.primary']} strokeWidth={ICON_STROKE_WIDTH} />
              }
              flexDirection="row"
              textAlign="center"
              justifyContent="center"
              style={[styles.button, { backgroundColor: theme.colors['text.emphasized'] }]}
            >
              <Text
                variant="bodyLargeStrong"
                color="text.inverse.primary"
                fontSize={18}
                lineHeight={24}
                fontWeight="700"
                textAlign="center"
                flexShrink={1}
              >
                {t('feed.endOfFeed.action')}
              </Text>
            </Button>
          </Animated.View>
        ) : null}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { alignItems: 'center', paddingHorizontal: 16, paddingTop: 24, paddingBottom: 40 },
  card: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    borderRadius: 24,
    overflow: 'hidden',
    borderWidth: 1,
    paddingVertical: 24,
    paddingHorizontal: 16,
  },
  confettiLayer: { position: 'absolute', top: 0, left: 0, right: 0, height: 260, overflow: 'hidden', zIndex: 2 },
  confettiParticle: { position: 'absolute', top: 44, left: '50%', width: 7, opacity: 0 },
  checkFrame: { width: 28, height: 28, alignItems: 'center', justifyContent: 'center' },
  successRing: { position: 'absolute', width: 28, height: 28, borderRadius: 14, borderWidth: 2, opacity: 0 },
  copyFrame: { width: '100%', alignItems: 'center' },
  actionFrame: { width: '100%', maxWidth: 360 },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  badgeText: { flexShrink: 1, fontSize: 13, lineHeight: 18 },
  artworkFrame: { width: '100%', alignItems: 'center', marginTop: 16, marginBottom: 12 },
  artwork: { width: '100%', maxWidth: 280, aspectRatio: 1.5 },
  title: { maxWidth: 360, fontSize: 34, lineHeight: 36, letterSpacing: -0.6 },
  description: { maxWidth: 360, marginTop: 12, fontSize: 17, lineHeight: 24 },
  button: { width: '100%', maxWidth: 360, minHeight: 56, borderRadius: 999, marginTop: 20 },
});
